import fs from 'fs'
import path from 'path'
import { versionsMapToMajor, versionToMajor, versionToNumber } from 'minecraft-renderer/src/lib/utils'

import { stopAllSounds } from '../basicSounds'
import { musicSystem } from './musicSystem'
import { computeEffectiveVolume, DEFAULT_ATTENUATION_DISTANCE } from './soundAttenuation'
import { parseSoundMapVariant, type SoundEntry } from './soundMapFormat'

interface SoundMeta {
  loadTimeout?: number
  format: string
  baseUrl: string
}

interface SoundData {
  volume: number
  path: string
}

interface SoundMapData {
  allSoundsMap: Record<string, Record<string, string>>
  soundsLegacyMap: Record<string, string[]>
  soundsMeta: SoundMeta
}

interface BlockSoundMap {
  [blockName: string]: string
}

interface ResourcePackSoundEntry {
  name: string
  type?: 'file' | 'event'
  weight?: number
  stream?: boolean
  volume?: number
  pitch?: number
  timeout?: number
  attenuation_distance?: number
}
interface ResourcePackSound { sounds: Array<string | ResourcePackSoundEntry>; replace?: boolean }
type ResourcePackSoundsJson = Record<string, ResourcePackSound>
type ResolvedSound = { url: string; volume: number; attenuationDistance: number; timeout?: number; pitch?: number }

function resourceLocation(value: string, namespace = 'minecraft') {
  const parts = value.includes(':') ? value.split(':') : [namespace, value]
  if (parts.length !== 2 || parts[0] === '.' || parts[0] === '..' || !/^[a-z0-9_.-]+$/.test(parts[0]) || !/^[a-z0-9_./-]+$/.test(parts[1]) || parts[1].startsWith('/') || parts[1].split('/').some(segment => segment === '.' || segment === '..' || !segment)) return undefined
  return { namespace: parts[0], name: parts[1], key: `${parts[0]}:${parts[1]}` }
}
const finiteValue = (value: number | undefined, fallback: number, min = 0) => typeof value === 'number' && Number.isFinite(value) && value >= min ? value : fallback

export class SoundMap {
  private readonly soundsPerName: Record<string, SoundEntry[]>
  soundsIdToName: Record<string, string>
  private readonly existingResourcePackPaths = new Set<string>()
  private packRevision = 0
  private activeResourcePackBasePath: string | undefined
  private activeResourcePackSoundsJson: ResourcePackSoundsJson | undefined
  noVersionIdMapping = false

  constructor (
    private readonly soundData: SoundMapData,
    private readonly version: string
  ) {
    // First try exact version match
    let soundsMap = soundData.allSoundsMap[this.version]

    if (!soundsMap) {
      // If no exact match, try major version
      const allSoundsMajor = versionsMapToMajor(soundData.allSoundsMap)
      soundsMap = allSoundsMajor[versionToMajor(version)]

      if (!soundsMap) {
        // If still no match, use first available mapping
        soundsMap = Object.values(allSoundsMajor)[0]
      }

      this.noVersionIdMapping = true
    }

    // Create both mappings
    this.soundsIdToName = {}
    this.soundsPerName = {}

    for (const [id, soundsStr] of Object.entries(soundsMap ?? {})) {
      const sounds = soundsStr.split(',').map(s => parseSoundMapVariant(s))

      const [idPart, namePart] = id.split(';')
      this.soundsIdToName[idPart] = namePart

      this.soundsPerName[namePart] = sounds
    }
  }

  async updateActiveResourcePackBasePath (basePath: string | undefined) {
    const revision = ++this.packRevision
    this.activeResourcePackBasePath = basePath
    this.activeResourcePackSoundsJson = undefined
    this.existingResourcePackPaths.clear()
    if (!basePath) return
    // BrowserFS and older fixtures may not support directory enumeration. Always try vanilla.
    let namespaces = ['minecraft']
    try {
      const names = await fs.promises.readdir(path.join(basePath, 'assets'))
      namespaces = [...new Set([...namespaces, ...names.filter(name => /^[a-z0-9_.-]+$/.test(name) && name !== '.' && name !== '..')])]
    } catch {}
    const events: ResourcePackSoundsJson = Object.create(null)
    await Promise.all(namespaces.map(async namespace => {
      try {
        const json = JSON.parse(await fs.promises.readFile(path.join(basePath, 'assets', namespace, 'sounds.json'), 'utf8'))
        for (const [key, value] of Object.entries(json)) {
          const location = resourceLocation(key, namespace)
          if (location && value && Array.isArray((value as ResourcePackSound).sounds)) events[location.key] = value as ResourcePackSound
        }
      } catch {}
    }))
    if (revision === this.packRevision) this.activeResourcePackSoundsJson = events
  }

  async updateExistingResourcePackPaths () {
    if (!this.activeResourcePackBasePath) return
    const basePath = this.activeResourcePackBasePath
    const namespaces = await fs.promises.readdir(path.join(basePath, 'assets'))
    for (const namespace of namespaces.filter(name => /^[a-z0-9_-]+$/.test(name))) {
      const root = path.join(basePath, 'assets', namespace, 'sounds')
      const scan = async (dir: string) => {
        const entries = await fs.promises.readdir(dir, { withFileTypes: true })
        for (const entry of entries) {
          const entryPath = path.join(dir, entry.name)
          if (entry.isDirectory()) await scan(entryPath)
          else if (entry.isFile() && entry.name.endsWith('.ogg')) this.existingResourcePackPaths.add(`${namespace}:${path.relative(root, entryPath)}`)
        }
      }
      try { await scan(root) } catch {}
    }
  }

  async getSoundUrl (soundKey: string, volume = 1): Promise<ResolvedSound | undefined> {
    return this.resolveEvent(soundKey, volume, new Set())
  }

  private async resolveEvent(soundKey: string, volume: number, visited: Set<string>): Promise<ResolvedSound | undefined> {
    const location = resourceLocation(soundKey)
    if (!location || visited.has(location.key) || visited.size >= 16) return undefined
    const nextVisited = new Set(visited).add(location.key)
    const event = this.activeResourcePackSoundsJson?.[location.key]
    if (event && this.activeResourcePackBasePath) {
      const variants = event.sounds.map(value => typeof value === 'string' ? { name: value } : value).filter(value => value && typeof value.name === 'string' && finiteValue(value.weight, 1) > 0)
      const totalWeight = variants.reduce((sum, value) => sum + finiteValue(value.weight, 1), 0)
      let random = Math.random() * totalWeight
      const sound = variants.find(value => { random -= finiteValue(value.weight, 1); return random < 0 })
      if (sound) {
        const gain = computeEffectiveVolume(finiteValue(sound.volume, 1), volume)
        const pitch = finiteValue(sound.pitch, 1, 0.01)
        if (sound.type === 'event') {
          const result = await this.resolveEvent(sound.name, gain, nextVisited)
          if (result) return { ...result, pitch: (result.pitch ?? 1) * pitch, attenuationDistance: sound.attenuation_distance === undefined ? result.attenuationDistance : finiteValue(sound.attenuation_distance, DEFAULT_ATTENUATION_DISTANCE, 0.01) }
        } else {
          const attenuationDistance = finiteValue(sound.attenuation_distance, DEFAULT_ATTENUATION_DISTANCE, 0.01)
          if (/^https?:\/\//.test(sound.name)) return { url: sound.name, volume: gain, attenuationDistance, timeout: sound.timeout, pitch }
          // As in Minecraft ResourceLocation, unqualified file and event names use minecraft.
          const file = resourceLocation(sound.name)
          if (file) {
            for (const format of [...new Set([this.soundData.soundsMeta.format, 'ogg'])]) {
              if (!/^[a-z0-9]+$/.test(format)) continue
              try {
                const resourcePath = path.join(this.activeResourcePackBasePath, 'assets', file.namespace, 'sounds', `${file.name}.${format}`)
                const bytes = await fs.promises.readFile(resourcePath)
                return { url: `data:audio/${format};base64,${bytes.toString('base64')}`, volume: gain, attenuationDistance, timeout: sound.timeout, pitch }
              } catch {}
            }
          }
        }
      }
      if (event.replace) return undefined
    }
    if (location.namespace !== 'minecraft') return undefined
    const sounds = this.soundsPerName[location.name]
    if (!sounds?.length) return undefined
    const totalWeight = sounds.reduce((sum, sound) => sum + sound.weight, 0)
    let random = Math.random() * totalWeight
    const sound = sounds.find(sound => { random -= sound.weight; return random < 0 }) ?? sounds[0]
    const versionedSound = this.getVersionedSound(sound.file)
    const url = this.soundData.soundsMeta.baseUrl.replace(/\/$/, '') + (versionedSound ? `/${versionedSound}` : '') + '/minecraft/sounds/' + sound.file + '.' + this.soundData.soundsMeta.format
    return { url, volume: computeEffectiveVolume(sound.volume, volume), attenuationDistance: sound.attenuationDistance, timeout: this.soundData.soundsMeta.loadTimeout }
  }

  private getVersionedSound (item: string): string | undefined {
    const verNumber = versionToNumber(this.version)
    const entries = Object.entries(this.soundData.soundsLegacyMap)
    for (const [itemsVer, items] of entries) {
      if (items.includes(item) && verNumber <= versionToNumber(itemsVer)) {
        return itemsVer
      }
    }
    return undefined
  }

  getBlockSound (blockName: string, category: string, fallback: string): string {
    const mappedName = blockSoundAliases[blockName] ?? blockName
    const key = `block.${mappedName}.${category}`
    return this.soundsPerName[key] ? key : fallback
  }

  getStepSound (blockName: string): string {
    return this.getBlockSound(blockName, 'step', 'block.stone.step')
  }

  getBreakSound (blockName: string): string {
    return this.getBlockSound(blockName, 'break', 'block.stone.break')
  }

  quit () {
    musicSystem.stopMusic()
    stopAllSounds()
  }
}

export function createSoundMap (version: string): SoundMap | null {
  const globalObject = window as {
    allSoundsMap?: Record<string, Record<string, string>>,
    allSoundsVersionedMap?: Record<string, string[]>,
    allSoundsMeta?: { format: string, baseUrl: string }
  }
  if (!globalObject.allSoundsMap) return null
  return new SoundMap({
    allSoundsMap: globalObject.allSoundsMap,
    soundsLegacyMap: globalObject.allSoundsVersionedMap ?? {},
    soundsMeta: globalObject.allSoundsMeta!
  }, version)
}

// Block name mappings for sound effects
const blockSoundAliases: BlockSoundMap = {
  // Grass-like blocks
  grass_block: 'grass',
  tall_grass: 'grass',
  fern: 'grass',
  large_fern: 'grass',
  dead_bush: 'grass',
  seagrass: 'grass',
  tall_seagrass: 'grass',
  kelp: 'grass',
  kelp_plant: 'grass',
  sugar_cane: 'grass',
  bamboo: 'grass',
  vine: 'grass',
  nether_sprouts: 'grass',
  twisting_vines: 'grass',
  weeping_vines: 'grass',
  sweet_berry_bush: 'grass',
  glow_lichen: 'grass',
  moss_carpet: 'grass',
  moss_block: 'grass',
  hanging_roots: 'grass',
  spore_blossom: 'grass',
  small_dripleaf: 'grass',
  big_dripleaf: 'grass',
  flowering_azalea: 'grass',
  azalea: 'grass',
  azalea_leaves: 'grass',
  flowering_azalea_leaves: 'grass',

  // Dirt and ground blocks
  dirt: 'gravel',
  coarse_dirt: 'gravel',
  podzol: 'gravel',
  mycelium: 'gravel',
  farmland: 'gravel',
  dirt_path: 'gravel',
  rooted_dirt: 'rooted_dirt',

  // Crop blocks
  wheat: 'crop',
  potatoes: 'crop',
  carrots: 'crop',
  beetroots: 'crop',
  melon_stem: 'crop',
  pumpkin_stem: 'crop',
  sweet_berries: 'crop',
  cocoa: 'crop',
  nether_wart: 'crop',
  torchflower_crop: 'crop',
  pitcher_crop: 'crop',

  // Stone-like blocks
  cobblestone: 'stone',
  stone_bricks: 'stone',
  mossy_stone_bricks: 'stone',
  cracked_stone_bricks: 'stone',
  chiseled_stone_bricks: 'stone',
  stone_brick_slab: 'stone',
  stone_brick_stairs: 'stone',
  stone_brick_wall: 'stone',
  polished_granite: 'stone',
  granite: 'stone',
  andesite: 'stone',
  diorite: 'stone',
  polished_andesite: 'stone',
  polished_diorite: 'stone',
  deepslate: 'deepslate',
  cobbled_deepslate: 'deepslate',
  polished_deepslate: 'deepslate',
  deepslate_bricks: 'deepslate_bricks',
  deepslate_tiles: 'deepslate_tiles',
  calcite: 'stone',
  tuff: 'stone',
  smooth_stone: 'stone',
  smooth_sandstone: 'stone',
  smooth_quartz: 'stone',
  smooth_red_sandstone: 'stone',

  // Wood-like blocks
  oak_planks: 'wood',
  spruce_planks: 'wood',
  birch_planks: 'wood',
  jungle_planks: 'wood',
  acacia_planks: 'wood',
  dark_oak_planks: 'wood',
  crimson_planks: 'wood',
  warped_planks: 'wood',
  oak_log: 'wood',
  spruce_log: 'wood',
  birch_log: 'wood',
  jungle_log: 'wood',
  acacia_log: 'wood',
  dark_oak_log: 'wood',
  crimson_stem: 'stem',
  warped_stem: 'stem',

  // Metal blocks
  iron_block: 'metal',
  gold_block: 'metal',
  copper_block: 'copper',
  exposed_copper: 'copper',
  weathered_copper: 'copper',
  oxidized_copper: 'copper',
  netherite_block: 'netherite_block',
  ancient_debris: 'ancient_debris',
  lodestone: 'lodestone',
  chain: 'chain',
  anvil: 'anvil',
  chipped_anvil: 'anvil',
  damaged_anvil: 'anvil',

  // Glass blocks
  glass: 'glass',
  glass_pane: 'glass',
  white_stained_glass: 'glass',
  orange_stained_glass: 'glass',
  magenta_stained_glass: 'glass',
  light_blue_stained_glass: 'glass',
  yellow_stained_glass: 'glass',
  lime_stained_glass: 'glass',
  pink_stained_glass: 'glass',
  gray_stained_glass: 'glass',
  light_gray_stained_glass: 'glass',
  cyan_stained_glass: 'glass',
  purple_stained_glass: 'glass',
  blue_stained_glass: 'glass',
  brown_stained_glass: 'glass',
  green_stained_glass: 'glass',
  red_stained_glass: 'glass',
  black_stained_glass: 'glass',
  tinted_glass: 'glass',

  // Wool blocks
  white_wool: 'wool',
  orange_wool: 'wool',
  magenta_wool: 'wool',
  light_blue_wool: 'wool',
  yellow_wool: 'wool',
  lime_wool: 'wool',
  pink_wool: 'wool',
  gray_wool: 'wool',
  light_gray_wool: 'wool',
  cyan_wool: 'wool',
  purple_wool: 'wool',
  blue_wool: 'wool',
  brown_wool: 'wool',
  green_wool: 'wool',
  red_wool: 'wool',
  black_wool: 'wool',

  // Nether blocks
  netherrack: 'netherrack',
  nether_bricks: 'nether_bricks',
  red_nether_bricks: 'nether_bricks',
  nether_wart_block: 'wart_block',
  warped_wart_block: 'wart_block',
  soul_sand: 'soul_sand',
  soul_soil: 'soul_soil',
  basalt: 'basalt',
  polished_basalt: 'basalt',
  blackstone: 'gilded_blackstone',
  gilded_blackstone: 'gilded_blackstone',

  // Amethyst blocks
  amethyst_block: 'amethyst_block',
  amethyst_cluster: 'amethyst_cluster',
  large_amethyst_bud: 'large_amethyst_bud',
  medium_amethyst_bud: 'medium_amethyst_bud',
  small_amethyst_bud: 'small_amethyst_bud',

  // Miscellaneous
  sand: 'sand',
  red_sand: 'sand',
  gravel: 'gravel',
  snow: 'snow',
  snow_block: 'snow',
  powder_snow: 'powder_snow',
  ice: 'glass',
  packed_ice: 'glass',
  blue_ice: 'glass',
  slime_block: 'slime_block',
  honey_block: 'honey_block',
  scaffolding: 'scaffolding',
  ladder: 'ladder',
  lantern: 'lantern',
  soul_lantern: 'lantern',
  pointed_dripstone: 'pointed_dripstone',
  dripstone_block: 'dripstone_block',
  sculk_sensor: 'sculk_sensor',
  shroomlight: 'shroomlight'
}
